/**
 * <group-selector>
 *
 * Generic "Choose your X Type" add-on group element. Used for chairs,
 * benches, panel storage, table accessories, and any future grouping
 * declared via `addon_group` inside each addon product's
 * `custom.modular_config` JSON metafield.
 *
 * For each group, the customer can:
 *   1. Increment/decrement a single quantity stepper (respects the underlying
 *      product's `quantity.is_even` metafield).
 *   2. Choose WHICH item type to add via a radio grid that slides in once
 *      the quantity goes above 0.
 *   3. For "external" items (e.g. table accessories) that have multiple
 *      color variants, pick a color from a swatch row inside the active
 *      card.
 *
 * Internally the element owns one hidden `<bundle-item>` (with an
 * `<addons-quantity>` inside) per item in the group. Only the currently
 * selected item's hidden `<addons-quantity>` ever holds a non-zero
 * quantity, so the existing subtotal aggregation (bundle-item.js) and cart
 * submission (product-form.js) keep working unchanged.
 *
 * Multiple <group-selector> instances coexist on the page (one per group);
 * because they all live inside `#UpsellProductList-{sectionId}`, their
 * quantities sum into the same bundle subtotal naturally.
 */
if (!customElements.get('group-selector')) {
  customElements.define(
    'group-selector',
    class GroupSelector extends HTMLElement {
      constructor() {
        super();

        this.step = parseInt(this.dataset.step, 10) || 1;
        this.maxQty = parseInt(this.dataset.maxQty, 10) || 20;
        this.sectionId = this.dataset.section;
        this.groupName = this.dataset.group;

        this.radioWrap = this.querySelector('[data-group-radio-wrap]');
        this.cardEl = this.querySelector('.group-selector-card');
        this.decBtn = this.querySelector('[data-group-decrement]');
        this.incBtn = this.querySelector('[data-group-increment]');
        this.qtyDisplay = this.querySelector('[data-group-qty]');
        this.titleEl = this.querySelector('[data-group-title]');
        this.priceEl = this.querySelector('[data-group-price]');
        this.viewProductEl = this.querySelector('[data-group-view-product]');
        this.swatchesEl = this.querySelector('[data-group-swatches]');
        this.swatchesWrapEl = this.querySelector('[data-group-swatches-wrap]');
        this.swatchTriggerEl = this.querySelector('[data-group-swatch-trigger]');
        this.swatchChevronEl = this.querySelector('[data-group-swatch-chevron]');
        this.activeSwatchEl = this.querySelector('[data-group-active-swatch]');
        // Active-card stepper vs "Sold out" label — toggled when the
        // selected item's parent-color variant becomes (un)available.
        this.stepperEl = this.querySelector('[data-group-stepper]');
        this.soldOutEl = this.querySelector('[data-group-sold-out]');
        // Small italic note shown when the best-match item is sold out
        // and we auto-switched to another available item.
        this.fallbackNoteEl = this.querySelector('[data-group-fallback-note]');
        this.fallbackNoteTemplate = this.dataset.fallbackNoteTemplate || '';
        // Per-item image blocks (one per item in the group). The block
        // structure matches bundle-product.liquid: a wrapper <div> with
        // an inner <ul> of <li id="Card_Product_Variant_Image--{id}">
        // elements (one per variant of that item).
        this.itemImageBlocks = Array.from(
          this.querySelectorAll('[data-group-item-image]')
        );

        this.hiddenItems = Array.from(
          this.querySelectorAll('[data-group-hidden-item]')
        );
        this.radioInputs = Array.from(
          this.querySelectorAll('input[type="radio"][data-group-radio]')
        );

        this.currentQty = 0;
        this.selectedHandle = this.dataset.defaultHandle;
        this.currentParentColorHandle = this.dataset.parentColor || '';
        // True once the customer explicitly clicks a radio. We then
        // stop auto-switching the active card so we never override a
        // deliberate choice (even if the choice is sold out).
        this.userManuallySelected = false;

        this.bindEvents();
        this.syncActiveCardFromSelected();
        this.updateAllRadioSoldOutStates();
        // Initial auto-pick: if the best match is sold out in the
        // current parent color at SSR, swap the active card over to an
        // item that IS available so the customer isn't blocked.
        this.autoPickAvailableItem();
      }

      bindEvents() {
        if (this.decBtn) {
          this.decBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.changeQty(this.currentQty - this.step);
          });
        }

        if (this.incBtn) {
          this.incBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.changeQty(this.currentQty + this.step);
          });
        }

        this.radioInputs.forEach((radio) => {
          radio.addEventListener('change', () => {
            if (radio.checked) {
              this.onItemSwitch(radio.dataset.itemHandle);
            }
          });
        });

        // Active swatch chip → toggles the popover.
        // Fires for any item with more than one variant (the chip's
        // `pointer-events-none` is only added by `renderSwatchesFor`
        // when the item is single-variant). The `hidden` class lives
        // on the outer wrapper so the inner <ul> can keep its flex
        // layout when the popover opens — check that, not the <ul>.
        if (this.swatchTriggerEl) {
          this.swatchTriggerEl.addEventListener('click', (e) => {
            e.stopPropagation();
            if (!this.swatchesWrapEl) return;
            if (this.swatchesWrapEl.classList.contains('hidden')) {
              this.openSwatchPopover();
            } else {
              this.closeSwatchPopover();
            }
          });
        }
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) {
            this.closeSwatchPopover();
            this.closeAllRadioSwatchPopovers();
          }
        });

        this.bindRadioCardSwatches();

        // Main product variant change. We mirror what
        // `bundle-item.js#updateVariantMedia` does for the What's Included
        // cards: swap which Card_Product_Variant_Image--{id} <li> is
        // visible inside every item's image block. We update *all* item
        // blocks (not just the active one) so switching item types later
        // still shows the correct color.
        document.addEventListener('variant:swatch', (event) => {
          const parentTitle = event && event.detail && event.detail.value;
          if (parentTitle) {
            this.currentParentColorHandle = this.handleize(parentTitle);
            this.swapVariantImagesForParentColor(parentTitle);
            this.updateAllRadioSoldOutStates();
            // Re-evaluate the active card. Will switch to a fallback
            // item if the current one is sold out in the new color
            // (unless the customer manually chose this item — then we
            // respect that choice and show "Sold out").
            this.autoPickAvailableItem();
          }
          if (this.currentQty !== 0) {
            this.changeQty(0);
          }
        });
      }

      // Resolves the "effective" variant for an item — used by
      // availability checks (stepper vs sold-out, radio sold-out
      // labels, auto-pick fallback) so they reflect what the customer
      // is actually about to buy.
      //
      // Order of preference:
      //   1. External products → the customer's currently-picked variant.
      //   2. Variant whose handleized title equals the current parent color.
      //   3. The item's current `variantId` (kept after a previous match
      //      or manual swatch click — e.g. TT chair stays on Scandinavian
      //      Oak when the parent switches to Royal Walnut).
      //   4. First variant (only used when nothing is initialized yet).
      findMatchedVariantForParent(itemData) {
        if (!itemData || !itemData.variants || itemData.variants.length === 0) {
          return null;
        }
        // External products always use whatever variant the customer picked.
        if (itemData.isExternal && itemData.variantId) {
          const current = itemData.variants.find(
            (v) => String(v.id) === String(itemData.variantId)
          );
          if (current) return current;
        }
        // Customer explicitly chose a color via a swatch popover on this
        // add-on. Use that variant for availability so picking e.g.
        // "Smoked Hickory" on a card whose best-match-parent-color is sold
        // out doesn't force a "Sold out" display on their chosen color.
        if (!itemData.isExternal && itemData.colorManual && itemData.variantId) {
          const chosen = itemData.variants.find(
            (v) => String(v.id) === String(itemData.variantId)
          );
          if (chosen) return chosen;
        }
        const parentHandle = this.currentParentColorHandle;
        const matched = itemData.variants.find(
          (v) => this.handleize(v.title || '') === parentHandle
        );
        if (matched) return matched;
        if (itemData.variantId) {
          const current = itemData.variants.find(
            (v) => String(v.id) === String(itemData.variantId)
          );
          if (current) return current;
        }
        return itemData.variants[0] || null;
      }

      // Active card: show "+/-" stepper when the currently selected
      // item has a variant available in the parent color, otherwise
      // hide it and show the red "Sold out" label.
      updateAvailabilityForSelected() {
        const wrap = this.getSelectedHiddenItem();
        if (!wrap) return;
        const data = this.readItemData(wrap);
        const matched = this.findMatchedVariantForParent(data);
        const available = matched ? matched.available !== false : true;
        this.applyAvailabilityUI(available);
      }

      applyAvailabilityUI(available) {
        if (this.stepperEl) {
          this.stepperEl.classList.toggle('hidden', !available);
        }
        if (this.soldOutEl) {
          this.soldOutEl.classList.toggle('hidden', available);
        }
        // If the item just became unavailable while qty > 0, drop the
        // quantity back to 0 so the cart submission doesn't try to add
        // a sold-out variant.
        if (!available && this.currentQty !== 0) {
          this.changeQty(0);
        }
      }

      // Iterates every radio card and toggles its per-card "Sold out"
      // label based on whether that item has a variant available in the
      // current parent color.
      updateAllRadioSoldOutStates() {
        this.hiddenItems.forEach((wrap) => {
          const data = this.readItemData(wrap);
          const matched = this.findMatchedVariantForParent(data);
          const available = matched ? matched.available !== false : true;
          const label = this.querySelector(
            `[data-group-radio-sold-out="${data.handle}"]`
          );
          if (label) {
            label.classList.toggle('hidden', available);
          }
        });
      }

      // Picks the variant of each item that matches the new parent color
      // and toggles the corresponding <li> visible (others hidden) in
      // BOTH the active card image block AND every radio card image
      // block, so the chair-type grid stays color-synced with the main
      // product (e.g. switching to Smoked Hickory updates Round /
      // Regular / Nova radio cards together).
      // Also updates each hidden bundle-item's `data-current-variant` so
      // the cart submits the correct variant id, and refreshes the
      // per-card swatch chip under each chair image.
      swapVariantImagesForParentColor(parentTitle) {
        const parentHandle = this.handleize(parentTitle);
        this.hiddenItems.forEach((wrap) => {
          const data = this.readItemData(wrap);
          if (!data.variants || data.variants.length === 0) return;

          // External products track their own (swatch-picked) variant
          // independent of the parent dining-set color. Skipping them
          // here preserves the customer's color choice when they later
          // swap the parent variant.
          if (data.isExternal) return;

          // Try to find the addon variant whose title matches the
          // parent's color. If no match, KEEP the current variant
          // (per spec: "TT chair has no Royal Walnut → stay on
          // Scandinavian Oak"). We deliberately don't fall back to
          // variants[0] anymore, otherwise switching parents would
          // reset an otherwise-fine current color.
          const matched = data.variants.find(
            (v) => this.handleize(v.title || '') === parentHandle
          );
          if (!matched) return;
          const matchedId = String(matched.id);

          // Sync stored variant to the new parent color and clear any
          // manual-color flag so availability reverts to the parent color.
          wrap.dataset.itemVariantId = matchedId;
          wrap.dataset.itemColorManual = 'false';

          // Active card image block.
          const activeBlock = this.querySelector(
            `[data-group-item-image="${data.handle}"]`
          );
          if (activeBlock) {
            activeBlock.querySelectorAll('li').forEach((li) => {
              li.classList.toggle(
                'hidden',
                li.id !== `Card_Product_Variant_Image--${matchedId}`
              );
            });
          }

          // Radio card image block — same pattern, different id prefix.
          const radioBlock = this.querySelector(
            `[data-group-radio-card-image="${data.handle}"]`
          );
          if (radioBlock) {
            radioBlock.querySelectorAll('li').forEach((li) => {
              li.classList.toggle(
                'hidden',
                li.id !== `Group_Radio_Variant_Image--${matchedId}`
              );
            });
          }

          // Radio card swatch chip under the image (Figma 26335:46541).
          // One <span id="Group_Radio_Variant_Swatch--{id}"> per variant
          // pre-rendered; toggle which one is visible to match.
          const radioSwatchBlock = this.querySelector(
            `[data-group-radio-card-swatch="${data.handle}"]`
          );
          if (radioSwatchBlock) {
            radioSwatchBlock.querySelectorAll('span[id^="Group_Radio_Variant_Swatch--"]').forEach((span) => {
              span.classList.toggle(
                'hidden',
                span.id !== `Group_Radio_Variant_Swatch--${matchedId}`
              );
            });
          }

          // Keep the radio card's popover "active" border in sync with
          // the new variant so when the customer opens it next, the
          // current color is highlighted.
          const radioPopover = this.querySelector(
            `[data-group-radio-swatch-popover="${data.handle}"]`
          );
          if (radioPopover) {
            radioPopover
              .querySelectorAll('button[data-group-radio-swatch-option]')
              .forEach((b) => {
                const isActive = b.dataset.variantId === matchedId;
                b.classList.toggle('border-secondary', isActive);
                b.classList.toggle('border-transparent', !isActive);
              });
          }

          wrap.dataset.itemVariantId = matchedId;
          const bundleItem = wrap.querySelector('bundle-item');
          if (bundleItem) {
            bundleItem.dataset.currentVariant = matchedId;
          }
        });
      }

      handleize(str) {
        return String(str || '')
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');
      }

      changeQty(newQty) {
        if (newQty < 0) newQty = 0;
        if (newQty > this.maxQty) newQty = this.maxQty;

        const previousQty = this.currentQty;
        this.currentQty = newQty;
        this.updateStepperUI();
        this.applyQtyToSelectedItem();

        const isOpen = this.radioWrap?.dataset.state === 'open';
        if (newQty > 0 && !isOpen) {
          this.openRadioSelector();
        } else if (newQty === 0 && isOpen) {
          this.closeRadioSelector();
        }

        this.updateCardBorder(newQty);

        if (previousQty !== newQty) {
          this.updateTitleQtyPrefix();
        }
      }

      updateCardBorder(qty) {
        if (!this.cardEl) return;
        if (qty > 0) {
          this.cardEl.classList.remove('border-[#d9d9d9]');
          this.cardEl.classList.add('border-secondary');
          this.cardEl.style.backgroundColor = 'rgba(57, 70, 148, 0.04)';
        } else {
          this.cardEl.classList.remove('border-secondary');
          this.cardEl.classList.add('border-[#d9d9d9]');
          this.cardEl.style.backgroundColor = '';
        }
      }

      onItemSwitch(newHandle) {
        if (!newHandle || newHandle === this.selectedHandle) return;

        // Customer explicitly picked this item — from now on we leave
        // the active card alone (no more auto fallback), and clear any
        // "we auto-switched" note from a prior fallback.
        this.userManuallySelected = true;
        this.clearFallbackNote();

        this.zeroOutAllHiddenItems();
        this.selectedHandle = newHandle;
        this.syncActiveCardFromSelected();
        this.applyQtyToSelectedItem();
      }

      // Switch the active card to `handle` WITHOUT marking the choice
      // as manual. Used by autoPickAvailableItem when the best-match
      // (or current) item is sold out in this color so we don't dead-
      // end the customer on a chair they can't actually buy.
      programmaticItemSwitch(handle) {
        if (!handle || handle === this.selectedHandle) return;

        this.radioInputs.forEach((r) => {
          r.checked = r.dataset.itemHandle === handle;
        });
        this.zeroOutAllHiddenItems();
        this.selectedHandle = handle;
        this.syncActiveCardFromSelected();
        this.applyQtyToSelectedItem();
      }

      // Auto-fallback logic for the active card.
      //
      // Behaviour:
      //   • Customer made a manual radio choice → respect it. Just sync
      //     the stepper/sold-out UI; never auto-switch and never show
      //     the fallback chip.
      //   • Best match IS available in the current color → switch back
      //     to it (in case we'd previously swapped to a fallback) and
      //     clear the chip.
      //   • Best match is sold out but another group item has an
      //     available matched variant → switch to that item and show
      //     the chip "Best match unavailable: <best match name>".
      //   • Nothing in the group is available → keep best match
      //     selected, show its sold-out state, no chip.
      autoPickAvailableItem() {
        if (this.userManuallySelected) {
          this.clearFallbackNote();
          this.updateAvailabilityForSelected();
          return;
        }

        const defaultHandle = this.dataset.defaultHandle;
        const defaultWrap = defaultHandle
          ? this.hiddenItems.find((w) => w.dataset.itemHandle === defaultHandle)
          : null;
        const defaultMatched = defaultWrap
          ? this.findMatchedVariantForParent(this.readItemData(defaultWrap))
          : null;
        const bestMatchAvailable = defaultMatched
          ? defaultMatched.available !== false
          : false;

        // Case 1: best match is available — always show it.
        if (bestMatchAvailable) {
          if (this.selectedHandle !== defaultHandle) {
            this.programmaticItemSwitch(defaultHandle);
          }
          this.clearFallbackNote();
          this.updateAvailabilityForSelected();
          return;
        }

        // Case 2: best match sold out — find any other item that IS
        // available in the current parent color.
        let target = null;
        for (let i = 0; i < this.hiddenItems.length; i += 1) {
          const wrap = this.hiddenItems[i];
          const handle = wrap.dataset.itemHandle;
          if (handle === defaultHandle) continue;
          const matched = this.findMatchedVariantForParent(
            this.readItemData(wrap)
          );
          if (matched && matched.available !== false) {
            target = handle;
            break;
          }
        }

        if (target) {
          if (this.selectedHandle !== target) {
            this.programmaticItemSwitch(target);
          }
          const bestMatchTitle =
            defaultWrap && defaultWrap.dataset.itemTitle
              ? defaultWrap.dataset.itemTitle
              : '';
          this.showFallbackNote(bestMatchTitle);
          return;
        }

        // Case 3: nothing in the group is available — fall through to
        // the best-match (will display its "Sold out" stepper state).
        if (defaultHandle && this.selectedHandle !== defaultHandle) {
          this.programmaticItemSwitch(defaultHandle);
        }
        this.clearFallbackNote();
        this.updateAvailabilityForSelected();
      }

      showFallbackNote(bestMatchTitle) {
        if (
          !this.fallbackNoteEl ||
          !this.fallbackNoteTemplate ||
          !bestMatchTitle
        ) {
          this.clearFallbackNote();
          return;
        }
        const textEl = this.fallbackNoteEl.querySelector(
          '[data-group-fallback-text]'
        );
        if (textEl) {
          textEl.textContent = this.fallbackNoteTemplate.replace(
            '__NAME__',
            bestMatchTitle
          );
        }
        this.fallbackNoteEl.classList.remove('hidden');
      }

      clearFallbackNote() {
        if (!this.fallbackNoteEl) return;
        const textEl = this.fallbackNoteEl.querySelector(
          '[data-group-fallback-text]'
        );
        if (textEl) textEl.textContent = '';
        this.fallbackNoteEl.classList.add('hidden');
      }

      zeroOutAllHiddenItems() {
        this.hiddenItems.forEach((wrap) => {
          const select = wrap.querySelector('select[name="quantity"]');
          if (select && select.value !== '0') {
            select.value = '0';
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      applyQtyToSelectedItem() {
        const selectedWrap = this.getSelectedHiddenItem();
        if (!selectedWrap) return;

        this.hiddenItems.forEach((wrap) => {
          if (wrap === selectedWrap) return;
          const sel = wrap.querySelector('select[name="quantity"]');
          if (sel && sel.value !== '0') {
            sel.value = '0';
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        const select = selectedWrap.querySelector('select[name="quantity"]');
        if (!select) return;

        const desiredValue = String(this.clampToAvailableOption(select, this.currentQty));
        if (select.value !== desiredValue) {
          select.value = desiredValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      clampToAvailableOption(select, qty) {
        const opts = Array.from(select.options).map((o) => parseInt(o.value, 10));
        if (opts.length === 0) return qty;
        if (opts.includes(qty)) return qty;
        let chosen = opts[0];
        opts.forEach((v) => {
          if (v <= qty && v > chosen) chosen = v;
        });
        return chosen;
      }

      getSelectedHiddenItem() {
        return this.hiddenItems.find(
          (wrap) => wrap.dataset.itemHandle === this.selectedHandle
        );
      }

      syncActiveCardFromSelected() {
        const wrap = this.getSelectedHiddenItem();
        if (!wrap) return;

        const data = this.readItemData(wrap);

        if (this.titleEl) {
          this.titleEl.dataset.baseTitle = data.title || '';
          this.updateTitleQtyPrefix();
        }

        // Toggle which per-item image block is visible.
        this.itemImageBlocks.forEach((block) => {
          block.classList.toggle(
            'hidden',
            block.dataset.groupItemImage !== this.selectedHandle
          );
        });

        // Also ensure the correct variant <li> is visible inside the
        // active item's image block. The user may have picked a color
        // via the radio-card swatch popover before switching the radio
        // selection, which updates wrap.dataset.itemVariantId but only
        // toggles the radio-card image block — not this active-card block.
        if (data.variantId) {
          const activeBlock = this.querySelector(
            `[data-group-item-image="${this.selectedHandle}"]`
          );
          if (activeBlock) {
            activeBlock.querySelectorAll('li').forEach((li) => {
              li.classList.toggle(
                'hidden',
                li.id !== `Card_Product_Variant_Image--${data.variantId}`
              );
            });
          }
        }

        if (this.priceEl) {
          // Skip the re-render on the very first sync IF we're still on
          // the SSR default item. `money.liquid` already stripped the
          // trailing zeros via `money_without_trailing_zeros | replace:
          // '.00', ''` (the SSR is correct), and re-rendering here
          // before `<addons-quantity>` has been upgraded would fall
          // through `formatMoney` to the dumb `cartStrings.money_format`
          // fallback which re-introduces `.00` (the merchant's localized
          // money template has it baked in). Once the user does anything
          // — pick a different item, swatch, color — we re-render via
          // the rich `addons-quantity#formatPrice` path (driven by
          // `window.internationalization.stores[].currency.trailingZeros`).
          const isInitialDefaultSync =
            !this._hasSyncedOnce &&
            this.selectedHandle === this.dataset.defaultHandle;
          if (!isInitialDefaultSync) {
            // If the user picked a specific variant (e.g. via radio-card
            // swatch popover), recompute the price for that variant so
            // the active card reflects the user's color choice.
            const currentVariant = (data.variants || []).find(
              (v) => String(v.id) === String(data.variantId)
            );
            if (currentVariant) {
              this.priceEl.innerHTML = this.formatVariantPriceHtml(
                currentVariant,
                data.discountType,
                parseFloat(data.discountAmount) || 0
              );
            } else {
              this.priceEl.innerHTML = data.priceHtml || '';
            }
          }
        }
        this._hasSyncedOnce = true;
        if (this.viewProductEl) {
          this.viewProductEl.dataset.upsellQvHandle = data.handle || '';
          this.viewProductEl.dataset.upsellQvVariant = data.variantId || '';
          this.viewProductEl.dataset.upsellQvTitle = data.title || '';
          this.viewProductEl.dataset.upsellQvDiscountType = data.discountType || '';
          this.viewProductEl.dataset.upsellQvDiscountAmount = data.discountAmount || '';
        }

        this.renderSwatchesFor(wrap, data);
        this.updateAvailabilityForSelected();
      }

      readItemData(wrap) {
        let variants = [];
        try {
          variants = JSON.parse(wrap.dataset.itemVariants || '[]');
        } catch (e) {
          variants = [];
        }
        return {
          handle: wrap.dataset.itemHandle || '',
          variantId: wrap.dataset.itemVariantId || '',
          title: wrap.dataset.itemTitle || '',
          image: wrap.dataset.itemImage || '',
          priceHtml: wrap.dataset.itemPriceHtml || '',
          discountType: wrap.dataset.itemDiscountType || '',
          discountAmount: wrap.dataset.itemDiscountAmount || '',
          isExternal: wrap.dataset.itemIsExternal === 'true',
          // True when the customer explicitly picked a color via a swatch
          // popover. When set, availability uses this variant directly
          // instead of the parent-color-matched one.
          colorManual: wrap.dataset.itemColorManual === 'true',
          variants,
        };
      }

      // Active card swatch UI (Figma 26335:46541). Always updates the
      // active chip image to reflect `wrap`'s current variant. For
      // external products with multiple variants, also populates the
      // popover so the customer can pick a different variant; for
      // non-external items the chevron + popover stay hidden.
      renderSwatchesFor(wrap, data) {
        const currentVariantId = String(
          wrap.dataset.itemVariantId || (data.variants[0] && data.variants[0].id) || ''
        );
        const currentVariant = (data.variants || []).find(
          (v) => String(v.id) === currentVariantId
        );

        // Update the active swatch chip image.
        if (this.activeSwatchEl) {
          const chipImg = this.activeSwatchEl.querySelector('img');
          if (chipImg && currentVariant && currentVariant.swatch_image) {
            chipImg.src = currentVariant.swatch_image;
            chipImg.removeAttribute('srcset');
          }
        }

        // Pickable whenever the item has more than one variant. The
        // popover lets the customer choose a different color (useful
        // when the parent product's color isn't carried by this
        // addon's collection — e.g. TT chair has no Royal Walnut).
        const isPickable = !!(data.variants && data.variants.length > 1);

        // Chevron + interactivity only for pickable (external) items.
        if (this.swatchChevronEl) {
          this.swatchChevronEl.classList.toggle('hidden', !isPickable);
        }
        if (this.swatchTriggerEl) {
          this.swatchTriggerEl.classList.toggle('pointer-events-none', !isPickable);
          if (isPickable) {
            this.swatchTriggerEl.removeAttribute('tabindex');
          } else {
            this.swatchTriggerEl.setAttribute('tabindex', '-1');
          }
        }

        if (!this.swatchesEl) return;

        if (!isPickable) {
          if (this.swatchesWrapEl) this.swatchesWrapEl.classList.add('hidden');
          this.swatchesEl.innerHTML = '';
          return;
        }

        // Build popover entries: one button per variant with a 24×24
        // bordered swatch — same visual language as the chip.
        const html = data.variants
          .map((v) => {
            if (!v.swatch_image) return '';
            const isActive = String(v.id) === currentVariantId;
            return (
              '<li>' +
              '<button type="button" class="addon-swatch-option block relative w-[24px] h-[24px] rounded-full border-[3px] border-solid ' +
              (isActive ? 'border-secondary' : 'border-transparent hover:border-[#787878]') +
              ' overflow-hidden" data-swatch-id="' + v.id + '" ' +
              'title="' + (v.title || '').replace(/"/g, '&quot;') + '" ' +
              'aria-label="' + (v.title || '').replace(/"/g, '&quot;') + '">' +
              '<img src="' + v.swatch_image + '" class="absolute inset-0 w-full h-full object-cover rounded-full" alt="' + (v.title || '').replace(/"/g, '&quot;') + '" />' +
              '</button>' +
              '</li>'
            );
          })
          .join('');

        this.swatchesEl.innerHTML = html;
        // Popover stays hidden until trigger is clicked.

        this.swatchesEl
          .querySelectorAll('button[data-swatch-id]')
          .forEach((btn) => {
            btn.addEventListener('click', (e) => {
              e.preventDefault();
              this.onVariantSwatchSelect(btn.dataset.swatchId);
              this.closeSwatchPopover();
            });
          });
      }

      openSwatchPopover() {
        if (this.swatchesWrapEl) this.swatchesWrapEl.classList.remove('hidden');
        if (this.swatchTriggerEl) {
          this.swatchTriggerEl.setAttribute('aria-expanded', 'true');
        }
      }

      closeSwatchPopover() {
        if (this.swatchesWrapEl) this.swatchesWrapEl.classList.add('hidden');
        if (this.swatchTriggerEl) {
          this.swatchTriggerEl.setAttribute('aria-expanded', 'false');
        }
      }

      // ── Per-radio-card swatch popovers ─────────────────────────────
      //
      // Each radio card has a chevron-triggered popover that lists all
      // of THAT item's variants. Picking a swatch here updates only
      // that card (chip + image + hidden bundle-item variant id) and,
      // if it happens to be the currently-selected item, also refreshes
      // the active card. Lets the customer pick a color from any
      // chair / bench / sideboard option without having to first
      // select the radio + open the active-card popover.
      bindRadioCardSwatches() {
        // Use event delegation on the group-selector element so every
        // trigger and option button is covered regardless of when the
        // popover becomes visible or any class changes.
        this.addEventListener('click', (e) => {
          // ── Swatch trigger (chevron chip on a radio card) ──────────
          const trigger = e.target.closest('[data-group-radio-swatch-trigger]');
          if (trigger && this.contains(trigger)) {
            e.preventDefault();
            e.stopPropagation();
            this.toggleRadioSwatchPopover(trigger.dataset.groupRadioSwatchTrigger);
            return;
          }

          // ── Swatch option inside a radio-card popover ───────────────
          const opt = e.target.closest('[data-group-radio-swatch-option]');
          if (opt && this.contains(opt)) {
            e.preventDefault();
            e.stopPropagation();
            const itemHandle = opt.dataset.groupRadioSwatchOption;
            const variantId = opt.dataset.variantId;
            this.onRadioCardSwatchSelect(itemHandle, variantId);
            this.closeRadioSwatchPopover(itemHandle);
            return;
          }
        });
      }

      toggleRadioSwatchPopover(itemHandle) {
        const wrap = this.querySelector(
          `[data-group-radio-swatch-popover-wrap="${itemHandle}"]`
        );
        if (!wrap) return;
        // Only one radio popover (and the active-card popover) open at
        // a time — close everything else first.
        this.closeAllRadioSwatchPopovers(itemHandle);
        this.closeSwatchPopover();
        if (!wrap.classList.contains('is-open')) {
          this.openRadioSwatchPopover(itemHandle);
        } else {
          this.closeRadioSwatchPopover(itemHandle);
        }
      }

      openRadioSwatchPopover(itemHandle) {
        const wrap = this.querySelector(
          `[data-group-radio-swatch-popover-wrap="${itemHandle}"]`
        );
        if (wrap) wrap.classList.add('is-open');
        const trigger = this.querySelector(
          `[data-group-radio-swatch-trigger="${itemHandle}"]`
        );
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        // The radio selector + card use overflow:hidden for their reveal
        // animations, which would clip this popover. Lift the clip while a
        // popover is open so it can overflow the card.
        this.setRadioCardClipOpen(true);
      }

      closeRadioSwatchPopover(itemHandle) {
        const wrap = this.querySelector(
          `[data-group-radio-swatch-popover-wrap="${itemHandle}"]`
        );
        if (wrap) wrap.classList.remove('is-open');
        const trigger = this.querySelector(
          `[data-group-radio-swatch-trigger="${itemHandle}"]`
        );
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        // Restore clipping once no radio popover remains open.
        if (!this.querySelector('[data-group-radio-swatch-popover-wrap].is-open')) {
          this.setRadioCardClipOpen(false);
        }
      }

      // Toggle the overflow clip on the radio selector wrap and the card so
      // an open swatch popover can escape, then restore it when closed.
      setRadioCardClipOpen(open) {
        if (this.radioWrap) this.radioWrap.style.overflow = open ? 'visible' : 'hidden';
        if (this.cardEl) this.cardEl.style.overflow = open ? 'visible' : 'hidden';
      }

      closeAllRadioSwatchPopovers(exceptHandle) {
        this.querySelectorAll('[data-group-radio-swatch-popover-wrap]').forEach((w) => {
          const handle = w.dataset.groupRadioSwatchPopoverWrap;
          if (handle === exceptHandle) return;
          this.closeRadioSwatchPopover(handle);
        });
      }

      // Customer picked a color from a specific radio card's popover.
      // Updates that card (chip + image + bundle-item variant id) and,
      // if the card is the active selection, refreshes the active card.
      onRadioCardSwatchSelect(itemHandle, variantId) {
        if (!itemHandle || !variantId) return;
        const wrap = this.hiddenItems.find(
          (w) => w.dataset.itemHandle === itemHandle
        );
        if (!wrap) return;

        const data = this.readItemData(wrap);
        const variant = (data.variants || []).find(
          (v) => String(v.id) === String(variantId)
        );
        if (!variant) return;

        // Update the stored variant id (this is what goes to cart when
        // this item is the selected one) and the bundle-item proxy.
        // Mark this item as manually color-chosen so availability checks
        // use this variant rather than the (potentially sold-out) parent color.
        wrap.dataset.itemVariantId = String(variantId);
        wrap.dataset.itemColorManual = 'true';
        if (variant.image) wrap.dataset.itemImage = variant.image;
        const bundleItem = wrap.querySelector('bundle-item');
        if (bundleItem) bundleItem.dataset.currentVariant = String(variantId);

        // Toggle the visible swatch chip on the radio card.
        const swatchBlock = this.querySelector(
          `[data-group-radio-card-swatch="${itemHandle}"]`
        );
        if (swatchBlock) {
          swatchBlock
            .querySelectorAll('span[id^="Group_Radio_Variant_Swatch--"]')
            .forEach((s) => {
              s.classList.toggle(
                'hidden',
                s.id !== `Group_Radio_Variant_Swatch--${variantId}`
              );
            });
        }

        // Toggle the visible variant image on the radio card.
        const imgBlock = this.querySelector(
          `[data-group-radio-card-image="${itemHandle}"]`
        );
        if (imgBlock) {
          imgBlock.querySelectorAll('li').forEach((li) => {
            li.classList.toggle(
              'hidden',
              li.id !== `Group_Radio_Variant_Image--${variantId}`
            );
          });
        }

        // Highlight the picked option inside this card's popover.
        const popover = this.querySelector(
          `[data-group-radio-swatch-popover="${itemHandle}"]`
        );
        if (popover) {
          popover
            .querySelectorAll('button[data-group-radio-swatch-option]')
            .forEach((b) => {
              const isActive = b.dataset.variantId === String(variantId);
              b.classList.toggle('border-secondary', isActive);
              b.classList.toggle('border-transparent', !isActive);
            });
        }

        // Update the "Sold out" label on this radio card to reflect the
        // newly chosen variant's availability. The user just picked a
        // specific color so we use the chosen variant directly.
        const soldOutLabel = this.querySelector(
          `[data-group-radio-sold-out="${itemHandle}"]`
        );
        if (soldOutLabel) {
          soldOutLabel.classList.toggle('hidden', variant.available !== false);
        }

        // If this card is the currently-selected one, also refresh the
        // active card (price, image, active chip).
        if (itemHandle === this.selectedHandle) {
          // Sync which item block is visible + chip + price.
          this.syncActiveCardFromSelected();
          // syncActiveCardFromSelected only toggles WHICH item block is
          // visible — the inner per-variant <li> doesn't change. We
          // just picked a new variant, so swap the visible <li> inside
          // the active item's image block too.
          const activeBlock = this.querySelector(
            `[data-group-item-image="${itemHandle}"]`
          );
          if (activeBlock) {
            activeBlock.querySelectorAll('li').forEach((li) => {
              li.classList.toggle(
                'hidden',
                li.id !== `Card_Product_Variant_Image--${variantId}`
              );
            });
          }
          // Recompute the active card's price for the picked variant.
          if (this.priceEl) {
            this.priceEl.innerHTML = this.formatVariantPriceHtml(
              variant,
              data.discountType,
              parseFloat(data.discountAmount) || 0
            );
          }
          // Reflect the picked variant's availability on the stepper.
          this.applyAvailabilityUI(variant.available !== false);
        }
      }

      onVariantSwatchSelect(variantId) {
        const wrap = this.getSelectedHiddenItem();
        if (!wrap || !variantId) return;

        const data = this.readItemData(wrap);
        const variant = data.variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return;

        // Highlight the active option inside the popover (new design).
        if (this.swatchesEl) {
          this.swatchesEl
            .querySelectorAll('button[data-swatch-id]')
            .forEach((b) => {
              const isActive = b.dataset.swatchId === String(variantId);
              b.classList.toggle('border-secondary', isActive);
              b.classList.toggle('border-transparent', !isActive);
            });
        }

        // Mirror the picked swatch into the active chip (Figma 26335:46541).
        if (this.activeSwatchEl && variant.swatch_image) {
          const chipImg = this.activeSwatchEl.querySelector('img');
          if (chipImg) {
            chipImg.src = variant.swatch_image;
            chipImg.removeAttribute('srcset');
          }
        }

        // Update the hidden bundle-item's current variant.
        // Mark as manually chosen so availability uses this variant.
        wrap.dataset.itemVariantId = String(variantId);
        wrap.dataset.itemColorManual = 'true';
        if (variant.image) {
          wrap.dataset.itemImage = variant.image;
        }
        const bundleItem = wrap.querySelector('bundle-item');
        if (bundleItem) {
          bundleItem.dataset.currentVariant = String(variantId);
        }

        // Toggle the matching <li> visible inside this item's image block.
        const block = this.querySelector(
          `[data-group-item-image="${this.selectedHandle}"]`
        );
        if (block) {
          block.querySelectorAll('li').forEach((li) => {
            li.classList.toggle(
              'hidden',
              li.id !== `Card_Product_Variant_Image--${variantId}`
            );
          });
        }

        if (this.viewProductEl) {
          this.viewProductEl.dataset.upsellQvVariant = String(variantId);
        }
        // Recompute price for the new variant using the same discount params.
        if (this.priceEl) {
          this.priceEl.innerHTML = this.formatVariantPriceHtml(
            variant,
            data.discountType,
            parseFloat(data.discountAmount) || 0
          );
        }

        // The active variant just changed — its `available` may differ
        // from the previous swatch, so reflect that in the stepper /
        // sold-out toggle.
        this.applyAvailabilityUI(variant.available !== false);
      }

      formatVariantPriceHtml(variant, discountType, discountAmount) {
        const price = parseInt(variant.price, 10) || 0;
        const compare = parseInt(variant.compare_at_price, 10) || 0;
        const fmt = (cents) => this.formatMoney(cents);

        if (discountType === ':percent' && discountAmount > 0) {
          const disc = Math.round((price * discountAmount) / 100);
          const final = price - disc;
          if (disc === 0 && compare > price) {
            return (
              '<div class="flex items-center gap-2">' +
              '<del class="font-body font-normal text-sm leading-[14px] text-[#787878] line-through">' +
              fmt(compare) +
              '</del>' +
              '<span class="font-body text-lg leading-[14px] font-semibold text-secondary">' +
              fmt(price) +
              '</span>' +
              '</div>'
            );
          }
          if (disc === 0) {
            return (
              '<div class="flex items-center gap-2">' +
              '<span class="font-body text-lg leading-[14px] font-semibold text-[#181D3A]">' +
              fmt(price) +
              '</span>' +
              '</div>'
            );
          }
          return (
            '<div class="flex items-center gap-2">' +
            '<del class="font-body font-normal text-sm leading-[14px] text-[#787878] line-through">' +
            fmt(price) +
            '</del>' +
            '<span class="font-body text-lg leading-[14px] font-semibold text-secondary">' +
            fmt(final) +
            '</span>' +
            '</div>'
          );
        }
        if (discountType === ':dollar' && discountAmount > 0) {
          // discountAmount is already in cents (matches the Liquid price atom
          // and the <addons-quantity> data-discount-amount contract).
          const final = price - discountAmount;
          return (
            '<div class="flex items-center gap-2">' +
            '<del class="font-body font-normal text-sm leading-[14px] text-[#787878] line-through">' +
            fmt(price) +
            '</del>' +
            '<span class="font-body text-lg leading-[14px] font-semibold text-secondary">' +
            fmt(final) +
            '</span>' +
            '</div>'
          );
        }
        if (compare > price) {
          return (
            '<div class="flex items-center gap-2">' +
            '<del class="font-body font-normal text-sm leading-[14px] text-[#787878] line-through">' +
            fmt(compare) +
            '</del>' +
            '<span class="font-body text-lg leading-[14px] font-semibold text-[#181D3A]">' +
            fmt(price) +
            '</span>' +
            '</div>'
          );
        }
        return (
          '<div class="flex items-center gap-2">' +
          '<span class="font-body text-lg leading-[14px] font-semibold text-[#181D3A]">' +
          fmt(price) +
          '</span>' +
          '</div>'
        );
      }

      formatMoney(cents) {
        // Delegate to <addons-quantity>.formatPrice when available so the
        // active card respects the merchant's localized currency formatting.
        const addonsEl = document.querySelector('addons-quantity');
        if (addonsEl && typeof addonsEl.formatPrice === 'function') {
          try {
            return addonsEl.formatPrice(cents / 100);
          } catch (e) {
            // fall through to plain format
          }
        }
        const fmt = (window.cartStrings && window.cartStrings.money_format) || '${{amount}}';
        const dollars = (cents / 100).toFixed(2);
        // Cover the comma-separator placeholders too — EUR stores use
        // €{{amount_with_comma_separator}}, which previously fell through
        // and rendered the raw placeholder text.
        return fmt
          .replace(/\{\{\s*amount\s*\}\}/, dollars)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, dollars.replace('.', ','))
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100));
      }

      updateStepperUI() {
        if (this.qtyDisplay) {
          this.qtyDisplay.textContent = String(this.currentQty);
        }
        if (this.decBtn) {
          this.decBtn.toggleAttribute('disabled', this.currentQty <= 0);
        }
        if (this.incBtn) {
          this.incBtn.toggleAttribute('disabled', this.currentQty >= this.maxQty);
        }
      }

      updateTitleQtyPrefix() {
        if (!this.titleEl) return;
        const baseTitle = this.titleEl.dataset.baseTitle || '';
        if (this.currentQty > 0) {
          this.titleEl.textContent = `x${this.currentQty} ${baseTitle}`;
        } else {
          this.titleEl.textContent = baseTitle;
        }
      }

      openRadioSelector() {
        if (!this.radioWrap) return;
        this.radioWrap.dataset.state = 'open';
        this.radioWrap.style.maxHeight = this.radioWrap.scrollHeight + 'px';
        this.radioWrap.style.opacity = '1';
        this.radioWrap.style.pointerEvents = 'auto';
        this.radioWrap.addEventListener(
          'transitionend',
          () => {
            if (this.radioWrap.dataset.state === 'open') {
              this.radioWrap.style.maxHeight = 'none';
            }
          },
          { once: true }
        );
      }

      closeRadioSelector() {
        if (!this.radioWrap) return;
        this.radioWrap.style.maxHeight = this.radioWrap.scrollHeight + 'px';
        requestAnimationFrame(() => {
          this.radioWrap.dataset.state = 'closed';
          this.radioWrap.style.maxHeight = '0px';
          this.radioWrap.style.opacity = '0';
          this.radioWrap.style.pointerEvents = 'none';
        });
      }
    }
  );
}
